import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import {
    AttendeeModel,
    ConfirmationAttendeesModel,
    ModalClass,
    NotificationIdentifierModel,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { FormsService, FormsState, getHasCopyForAttendee } from '@evorait/pace-forms';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of, Subscription } from 'rxjs';
import { map, mergeMap, take, tap } from 'rxjs/operators';
import { ConfirmationAddAttendeeComponent } from './confirmation-add-attendee/confirmation-add-attendee.component';

@Component({
    selector: 'pace-confirmation-attendees',
    templateUrl: './confirmation-attendees.component.html',
    styleUrls: ['./confirmation-attendees.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationAttendeesComponent implements OnInit, OnDestroy {
    @Input()
    public model: ConfirmationAttendeesModel;

    @Input()
    public notificationId: NotificationIdentifierModel;

    @Input()
    public parentForm: FormArray;

    @Output()
    public savedAttendee = new EventEmitter<void>();

    public formSubmitted$: Observable<boolean>;

    public faPencil = faPencilAlt;

    private addAttendeeSubscription?: Subscription;
    private editAttendeeSubscription?: Subscription;

    public attendeesForm: FormGroup;
    public attendeesFormChildren: FormArray;

    public constructor(
        private readonly formBuilder: FormBuilder,
        private readonly modalService: BsModalService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly translate: TranslateService,
        private readonly formsService: FormsService,
        private readonly store: Store<FormsState>,
    ) {}

    public ngOnInit(): void {
        this.formSubmitted$ = this.formsService
            .checkFormStatus(this.notificationId)
            .pipe(map((formStatusWarning) => !formStatusWarning.noFormSubmitted));

        const attendeesFormValidator = (): ValidationErrors | null => this.checkValidity();
        this.attendeesFormChildren = this.formBuilder.array([]);
        this.attendeesForm = this.formBuilder.group(
            {
                children: this.attendeesFormChildren,
            },
            {
                validators: attendeesFormValidator,
            },
        );

        this.parentForm.push(this.attendeesForm);
    }

    public ngOnDestroy(): void {
        if (typeof this.addAttendeeSubscription !== 'undefined') {
            this.addAttendeeSubscription.unsubscribe();
        }

        if (typeof this.editAttendeeSubscription !== 'undefined') {
            this.editAttendeeSubscription.unsubscribe();
        }
    }

    public deleteAttendee(index: number): void {
        const attendee = this.model.attendeeList.getModelByIndex(index);
        this.store
            .select(
                getHasCopyForAttendee({
                    participantId: attendee.id,
                }),
            )
            .pipe(
                take(1),
                mergeMap((hasCopy) => {
                    if (hasCopy) {
                        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
                            backdrop: 'static',
                            class: ModalClass.MODAL_MEDIUM,
                            initialState: {
                                model: new YesNoModalDialogModel(
                                    <string>this.translate.instant('CONFIRMATION_DELETE_ATTENDEE_TITLE'),
                                    <string>this.translate.instant('CONFIRMATION_DELETE_ATTENDEE_MESSAGE'),
                                    <string>this.translate.instant('CONFIRMATION_DELETE_ATTENDEE_YES'),
                                    <string>this.translate.instant('CONFIRMATION_DELETE_ATTENDEE_NO'),
                                ),
                            },
                            ariaLabelledBy: 'yes-no-modal-title',
                        });

                        return modalInstance.content?.modalResult ?? of(false);
                    } else {
                        return of(true);
                    }
                }),
                take(1),
                tap((removeAttendee) => {
                    if (removeAttendee) {
                        this.removeAttendee(index);
                    }
                }),
            )
            .subscribe();
    }

    public addAttendee(): void {
        const attendee = new AttendeeModel();

        const modalInstance = this.modalService.show(ConfirmationAddAttendeeComponent, {
            backdrop: 'static',
            class: 'modal-md',
            initialState: {
                model: attendee,
                edit: false,
            },
            ariaLabelledBy: 'confirmation-add-attendee-modal-title',
        });

        if (typeof this.addAttendeeSubscription !== 'undefined') {
            this.addAttendeeSubscription.unsubscribe();
        }

        this.addAttendeeSubscription = modalInstance.content?.modelChanged
            .pipe(take(1))
            .subscribe((attendeeModel: AttendeeModel) => {
                this.model.attendeeList.addModel(attendeeModel);
                this.attendeesForm.updateValueAndValidity();
                this.changeDetectorRef.markForCheck();
                this.changeDetectorRef.detectChanges();
                this.savedAttendee.emit();
            });
    }

    public editAttendee(index: number): void {
        const modalInstance = this.modalService.show(ConfirmationAddAttendeeComponent, {
            backdrop: 'static',
            class: 'modal-md',
            initialState: {
                model: this.model.attendeeList.value[index],
                edit: true,
            },
            ariaLabelledBy: 'confirmation-add-attendee-modal-title',
        });

        if (typeof this.editAttendeeSubscription !== 'undefined') {
            this.editAttendeeSubscription.unsubscribe();
        }

        this.editAttendeeSubscription = modalInstance.content?.modelChanged
            .pipe(take(1))
            .subscribe((attendeeModel: AttendeeModel) => {
                this.model.attendeeList.value[index] = attendeeModel;
                this.attendeesForm.updateValueAndValidity();
                this.changeDetectorRef.detectChanges();
                this.savedAttendee.emit();
            });
    }

    private checkValidity(): null | ValidationErrors {
        const errors: ValidationErrors = {};

        if (this.model.attendeeList.value.length === 0) {
            errors.required = 'At least one attendee is needed';
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    private removeAttendee(index: number): void {
        this.model.attendeeList.removeModel(index);
        this.attendeesForm.updateValueAndValidity();
        this.savedAttendee.emit();
    }
}
